import { useEffect, useMemo, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { ProductDto, ProductUSA } from "@/types/api/api-types";
import { useWineColorOptions } from "@/hooks/useEnumOptions";
import { useTranslation } from "react-i18next";

export type ProductProfilePickerDrawerProps = {
  open: boolean;
  onClose: () => void;
  products: ProductDto[] | ProductUSA[] | undefined;
  onSelectProduct: (productId: string) => void;
  isUsaProducts?: boolean;
  companyId?: string;
  defaultColor?: string;
};

const sortByDescription = <T extends { description?: string }>(list: T[]) =>
  [...list].sort((a, b) =>
    (a.description || "").localeCompare(b.description || "", "fr", { sensitivity: "base" }),
  );

const filterByQuery = <T extends { description?: string; descriptionPelican?: string }>(
  list: T[],
  query: string,
) => {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter((p) => {
    const d = (p.description || "").toLowerCase();
    const dp = (p.descriptionPelican || "").toLowerCase();
    return d.includes(q) || dp.includes(q);
  });
};

export const ProductProfilePickerDrawer = ({
  open,
  onClose,
  products,
  onSelectProduct,
  isUsaProducts,
  companyId,
  defaultColor,
}: ProductProfilePickerDrawerProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const wineColorOptions = useWineColorOptions();
  const [query, setQuery] = useState("");
  const [colorFilter, setColorFilter] = useState("");

  useEffect(() => {
    if (open) {
      setColorFilter(defaultColor ?? "");
    }
  }, [open, defaultColor]);

  const sortedAndFiltered = useMemo(() => {
    const filtered = filterByQuery(sortByDescription(products ?? []), query);
    if (!colorFilter) return filtered;
    return filtered.filter((p) => p.color === colorFilter);
  }, [products, query, colorFilter]);

  const groupedByClient = useMemo(() => {
    if (!isUsaProducts) return null;
    const list = sortedAndFiltered as ProductUSA[];
    return {
      client: list.filter((p) => p.client?.id === companyId),
      generic: list.filter((p) => !p.client),
      other: list.filter((p) => p.client && p.client.id !== companyId),
    };
  }, [sortedAndFiltered, isUsaProducts, companyId]);

  const PRODUCT_GROUPS = [
    {
      value: "client",
      label: t("productProfilePicker.groups.client"),
      items: groupedByClient?.client || [],
    },
    {
      value: "generic",
      label: t("productProfilePicker.groups.generic"),
      items: groupedByClient?.generic || [],
    },
    {
      value: "other",
      label: t("productProfilePicker.groups.other"),
      items: groupedByClient?.other || [],
    },
  ];

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setQuery("");
          onClose();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/50",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "bg-background fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-lg flex-col border-l shadow-xl duration-300",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
          )}
        >
          <DialogPrimitive.Close className="absolute top-4 right-4 z-10 rounded-sm opacity-70 transition-opacity hover:opacity-100">
            <X className="h-5 w-5" />
          </DialogPrimitive.Close>

          <div className="border-b px-6 py-4 pr-14">
            <DialogPrimitive.Title className="text-lg font-semibold">
              {t("productProfilePicker.title")}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-gray-500">
              {t("productProfilePicker.description")}
            </DialogPrimitive.Description>
          </div>

          <div className="border-b px-6 py-3">
            <div className="flex flex-row items-center gap-2">
              <UnifiedInput
                autoFocus
                inputType="text"
                leftElement={<Search />}
                value={query}
                onChange={(value) => setQuery(String(value ?? ""))}
                placeholder={t("productProfilePicker.searchPlaceholder")}
                wrapperClassName="flex-1 h-[2.4rem]"
              />
              <SelectV2
                value={colorFilter}
                onChange={setColorFilter}
                options={wineColorOptions}
                placeholder={t("productProfilePicker.colorPlaceholder")}
                clearable
                wrapperClassName="w-36 shrink-0"
                triggerClassName="h-9"
              />
            </div>
          </div>

          <div className="mt-3 flex flex-1 flex-col gap-1 overflow-y-auto px-6">
            {groupedByClient ? (
              <Accordion
                type="multiple"
                defaultValue={["client", "generic", "other"]}
                className="w-full"
              >
                {PRODUCT_GROUPS.map(({ value, label, items }) => (
                  <AccordionItem key={value} value={value}>
                    <AccordionTrigger className="">
                      {t("productProfilePicker.groupTitle", { label, count: items.length })}
                    </AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-1">
                      {items.map((p) => (
                        <ProductProfileButton
                          key={p.id}
                          product={p}
                          onSelect={() => {
                            onSelectProduct(p.id);
                            setQuery("");
                            onClose();
                          }}
                        />
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              sortedAndFiltered.map((p) => (
                <ProductProfileButton
                  key={p.id}
                  product={p}
                  onSelect={() => {
                    onSelectProduct(p.id);
                    setQuery("");
                    onClose();
                  }}
                />
              ))
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

const ProductProfileButton = ({
  product,
  onSelect,
}: {
  product: ProductDto | ProductUSA;
  onSelect: () => void;
}) => (
  <Button
    type="button"
    variant="outline"
    className="h-auto min-h-[3.5rem] w-full flex-col items-start justify-center gap-0.5 py-3 text-left whitespace-normal"
    onClick={onSelect}
  >
    <span className="w-full font-medium">{product.description}</span>
    <span className="text-muted-foreground w-full text-sm font-normal">
      {product.descriptionPelican}
    </span>
  </Button>
);
